import dayjs from "dayjs";

const WEATHER_FORECAST_CONFIG_KEY = "weather_forecast";

const normalizeWeatherModel = (model) =>
  typeof model === "string" && model.trim() ? model.trim() : null;

export const getWeatherForecastConfig = (siteMetadata) => {
  const config = siteMetadata?.[WEATHER_FORECAST_CONFIG_KEY];
  if (!config || typeof config !== "object") return null;

  if (Array.isArray(config.tiers)) {
    const tiers = config.tiers
      .map((tier) => {
        if (!tier || typeof tier !== "object") return null;
        const model = normalizeWeatherModel(tier.model);
        const days = Number(tier.days);
        return {
          model,
          days: Number.isFinite(days) && days > 0 ? Math.floor(days) : null,
        };
      })
      .filter(Boolean);

    if (tiers.some((tier) => tier.model)) return { tiers };
  }

  const fineModel = normalizeWeatherModel(config.fine_model);
  const extendedModel = normalizeWeatherModel(config.extended_model);
  const fineForecastDays = Number(config.fine_forecast_days);

  if (!fineModel && !extendedModel) return null;

  return {
    tiers: [
      {
        model: fineModel,
        days: Number.isFinite(fineForecastDays)
          ? Math.max(0, Math.floor(fineForecastDays))
          : 2,
      },
      { model: extendedModel, days: null },
    ],
  };
};

const buildOpenMeteoForecastUrl = ({
  lat,
  lon,
  startDate,
  endDate,
  timezone,
  model,
}) => {
  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set("latitude", lat);
  url.searchParams.set("longitude", lon);
  url.searchParams.set("start_date", startDate);
  url.searchParams.set("end_date", endDate);
  url.searchParams.set("daily", "temperature_2m_max,temperature_2m_min");
  url.searchParams.set("timezone", timezone);
  if (model) url.searchParams.set("models", model);
  return url.toString();
};

const mapOpenMeteoDailyTemperature = (data, dateFormat) => {
  if (!data?.daily?.time) {
    throw new Error("Invalid forecast data format received from API.");
  }
  return data.daily.time.map((date, index) => ({
    date: dayjs(date).format(dateFormat),
    max: data.daily.temperature_2m_max[index],
    min: data.daily.temperature_2m_min[index],
  }));
};

const addForecastRequest = ({
  fetchPromises,
  lat,
  lon,
  startDate,
  endDate,
  timezone,
  model,
  dateFormat,
}) => {
  const url = buildOpenMeteoForecastUrl({
    lat,
    lon,
    startDate,
    endDate,
    timezone,
    model,
  });

  fetchPromises.push(
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Forecast API responded with status ${response.status}`);
        }
        return response.json();
      })
      .then((data) => mapOpenMeteoDailyTemperature(data, dateFormat)),
  );
};

export const addOpenMeteoForecastRequests = ({
  fetchPromises,
  lat,
  lon,
  forecastStart,
  forecastEnd,
  timezone,
  siteMetadata,
  dateFormat,
}) => {
  if (forecastStart.isAfter(forecastEnd)) return;

  const forecastConfig = getWeatherForecastConfig(siteMetadata);

  if (!forecastConfig) {
    addForecastRequest({
      fetchPromises,
      lat,
      lon,
      startDate: forecastStart.format(dateFormat),
      endDate: forecastEnd.format(dateFormat),
      timezone,
      dateFormat,
    });
    return;
  }

  let tierStart = forecastStart;
  for (const tier of forecastConfig.tiers) {
    if (tierStart.isAfter(forecastEnd)) break;
    const tierBoundary = tier.days
      ? tierStart.add(tier.days - 1, "day")
      : forecastEnd;
    const tierEnd = tierBoundary.isBefore(forecastEnd)
      ? tierBoundary
      : forecastEnd;

    addForecastRequest({
      fetchPromises,
      lat,
      lon,
      startDate: tierStart.format(dateFormat),
      endDate: tierEnd.format(dateFormat),
      timezone,
      model: tier.model,
      dateFormat,
    });
    tierStart = tierEnd.add(1, "day");
  }

  if (!tierStart.isAfter(forecastEnd)) {
    addForecastRequest({
      fetchPromises,
      lat,
      lon,
      startDate: tierStart.format(dateFormat),
      endDate: forecastEnd.format(dateFormat),
      timezone,
      dateFormat,
    });
  }
};
